import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { GlobalSearchActionsService } from './global-search-actions.service';
import {
  cleanGlobalSearchText,
  GlobalSearchAction,
  GlobalSearchFlatRow,
  GlobalSearchGroup,
  GlobalSearchItem
} from './global-search.models';
import { GlobalSearchService } from './global-search.service';
import {
  isSpeechRecognitionSupported,
  parseVoiceCommand
} from './global-search-voice';

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss']
})
export class GlobalSearchComponent implements OnDestroy {
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  open = false;
  query = '';
  loading = false;
  groups: GlobalSearchGroup[] = [];
  flatRows: GlobalSearchFlatRow[] = [];
  activeIndex = 0;
  recent: string[] = [];
  actionMenuIndex: number | null = null;
  voiceSupported = isSpeechRecognitionSupported();
  listening = false;
  voiceStatus = '';
  private recognition: any = null;
  private readonly query$ = new Subject<string>();
  private sub: Subscription;
  private voiceSub: Subscription;

  readonly shortcuts: GlobalSearchItem[] = [
    {
      id: 'sc-qa',
      type: 'page',
      title: 'Open QA',
      subtitle: 'Quality Assurance queue',
      route: { path: '/qa' },
      actions: [{ id: 'navigate', label: 'Open', primary: true, payload: { path: '/qa' } }]
    },
    {
      id: 'sc-errors',
      type: 'page',
      title: 'Open Error Queue',
      subtitle: 'Errored reports',
      route: { path: '/qa/errors' },
      actions: [{ id: 'navigate', label: 'Open', primary: true, payload: { path: '/qa/errors' } }]
    },
    {
      id: 'sc-order',
      type: 'page',
      title: 'Order Report',
      subtitle: 'Start a screening order',
      route: { path: '/subscriber-reports/order-reports' },
      actions: [{ id: 'navigate', label: 'Open', primary: true, payload: { path: '/subscriber-reports/order-reports' } }]
    },
    {
      id: 'sc-subs',
      type: 'page',
      title: 'Subscribers',
      subtitle: 'Manage accounts',
      route: { path: '/subscribers/manage-subscriber' },
      actions: [{ id: 'navigate', label: 'Open', primary: true, payload: { path: '/subscribers/manage-subscriber' } }]
    },
    {
      id: 'sc-settings',
      type: 'page',
      title: 'Settings',
      subtitle: 'Platform configuration',
      route: { path: '/admin-settings' },
      actions: [{ id: 'navigate', label: 'Open', primary: true, payload: { path: '/admin-settings' } }]
    }
  ];

  constructor(
    private searchService: GlobalSearchService,
    public actions: GlobalSearchActionsService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    this.sub = this.query$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => {
        this.loading = true;
        this.actionMenuIndex = null;
      }),
      switchMap((q) => this.searchService.search(q))
    ).subscribe((res) => {
      this.loading = false;
      this.groups = res.groups || [];
      this.rebuildFlat();
    }, () => {
      this.loading = false;
    });
  }

  ngOnDestroy() {
    this.stopVoice();
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.voiceSub) {
      this.voiceSub.unsubscribe();
    }
    this.query$.complete();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent) {
    const key = event.key;
    const isModK = (event.ctrlKey || event.metaKey) && (key === 'k' || key === 'K');
    if (isModK) {
      event.preventDefault();
      this.toggle(true);
      return;
    }

    if (!this.open) {
      const target = event.target as HTMLElement;
      const tag = (target && target.tagName || '').toLowerCase();
      const editable = tag === 'input' || tag === 'textarea' || tag === 'select' || (target && target.isContentEditable);
      if (key === '/' && !editable && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        this.toggle(true);
      }
      return;
    }

    if (key === 'Escape') {
      event.preventDefault();
      if (this.actionMenuIndex != null) {
        this.actionMenuIndex = null;
      } else {
        this.dismiss();
      }
      return;
    }

    if (key === 'ArrowDown') {
      event.preventDefault();
      this.moveActive(1);
      return;
    }
    if (key === 'ArrowUp') {
      event.preventDefault();
      this.moveActive(-1);
      return;
    }
    if (key === 'Enter') {
      event.preventDefault();
      this.activateCurrent();
      return;
    }
    if (key === 'ArrowRight') {
      event.preventDefault();
      this.actionMenuIndex = this.activeIndex;
    }
  }

  openPalette() {
    this.toggle(true);
  }

  close() {
    this.stopVoice();
    this.open = false;
    this.actionMenuIndex = null;
    this.voiceStatus = '';
  }

  /** Esc / backdrop: if the search was cleared, drop the global-search record filter so the page shows its full list again. */
  dismiss() {
    this.close();
    if (!(this.query || '').trim()) {
      const tree = this.router.parseUrl(this.router.url);
      if (tree.queryParams.gsId) {
        delete tree.queryParams.gsId;
        this.router.navigateByUrl(tree);
      }
    }
  }

  toggle(forceOpen?: boolean) {
    this.open = forceOpen != null ? forceOpen : !this.open;
    if (this.open) {
      this.recent = this.searchService.getRecent();
      this.query = this.query || '';
      this.query$.next(this.query);
      setTimeout(() => {
        if (this.searchInput && this.searchInput.nativeElement) {
          this.searchInput.nativeElement.focus();
          this.searchInput.nativeElement.select();
        }
      }, 0);
    } else {
      this.stopVoice();
    }
  }

  toggleVoice(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.voiceSupported) {
      this.voiceStatus = 'Voice search is not supported in this browser.';
      return;
    }
    if (this.listening) {
      this.stopVoice();
      return;
    }
    this.startVoice();
  }

  private startVoice() {
    const w = window as any;
    const SpeechRecognition = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      this.voiceSupported = false;
      this.voiceStatus = 'Voice search is not supported in this browser.';
      return;
    }

    this.stopVoice();
    this.recognition = new SpeechRecognition();
    this.recognition.lang = 'en-US';
    this.recognition.interimResults = true;
    this.recognition.maxAlternatives = 3;
    this.recognition.continuous = false;

    this.listening = true;
    this.voiceStatus = 'Listening… say “open QA” or a name';

    this.recognition.onresult = (event: any) => {
      this.ngZone.run(() => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        transcript = (transcript || '').trim();
        if (!transcript) {
          return;
        }

        const isFinal = event.results[event.results.length - 1].isFinal;
        this.query = transcript;
        this.voiceStatus = isFinal ? `Heard: “${transcript}”` : `Hearing: “${transcript}”`;
        this.cdr.markForCheck();

        if (isFinal) {
          this.handleVoiceTranscript(transcript);
        }
      });
    };

    this.recognition.onerror = (event: any) => {
      this.ngZone.run(() => {
        const code = event && event.error;
        if (code === 'not-allowed') {
          this.voiceStatus = 'Microphone permission blocked.';
        } else if (code === 'no-speech') {
          this.voiceStatus = 'No speech detected. Try again.';
        } else if (code !== 'aborted') {
          this.voiceStatus = 'Voice search failed. Try again.';
        }
        this.listening = false;
        this.cdr.markForCheck();
      });
    };

    this.recognition.onend = () => {
      this.ngZone.run(() => {
        this.listening = false;
        this.cdr.markForCheck();
      });
    };

    try {
      this.recognition.start();
    } catch {
      this.listening = false;
      this.voiceStatus = 'Could not start microphone.';
      this.cdr.markForCheck();
    }
  }

  private stopVoice() {
    this.listening = false;
    if (this.recognition) {
      try {
        this.recognition.onresult = null;
        this.recognition.onerror = null;
        this.recognition.onend = null;
        this.recognition.abort();
      } catch {
        // ignore
      }
      this.recognition = null;
    }
  }

  private handleVoiceTranscript(transcript: string) {
    const parsed = parseVoiceCommand(transcript);
    this.searchService.rememberRecent(parsed.raw);

    // High-confidence page command → open immediately
    if (parsed.matchedItem && parsed.confidence >= 0.7) {
      this.voiceStatus = `Opening ${parsed.matchedItem.title}…`;
      this.actions.runPrimary(parsed.matchedItem);
      this.close();
      return;
    }

    // Otherwise search with cleaned phrase; auto-open if one strong page/queue hit
    const searchQ = parsed.cleaned || parsed.raw;
    this.query = searchQ;
    this.loading = true;
    this.voiceStatus = `Searching “${searchQ}”…`;

    if (this.voiceSub) {
      this.voiceSub.unsubscribe();
    }
    this.voiceSub = this.searchService.search(searchQ).subscribe((res) => {
      this.loading = false;
      this.groups = res.groups || [];
      this.rebuildFlat();

      const best = this.pickBestVoiceTarget(searchQ);
      if (best && (parsed.wantsOpen || best.score >= 0.85)) {
        this.voiceStatus = `Opening ${best.item.title}…`;
        this.searchService.rememberRecent(searchQ);
        this.actions.runPrimary(best.item);
        this.close();
        return;
      }

      this.voiceStatus = this.flatRows.length
        ? `Showing results for “${searchQ}”`
        : `No match for “${searchQ}”`;
      this.query$.next(searchQ);
    }, () => {
      this.loading = false;
      this.voiceStatus = 'Search failed after voice input.';
      this.query$.next(searchQ);
    });
  }

  private pickBestVoiceTarget(q: string): { item: GlobalSearchItem; score: number } | null {
    const needle = (q || '').toLowerCase();
    let best: { item: GlobalSearchItem; score: number } | null = null;
    (this.flatRows || []).forEach((row) => {
      const item = row.item;
      if (item.type !== 'page' && item.type !== 'queue') {
        return;
      }
      const hay = [item.title, item.subtitle, ...((item as any).keywords || [])].join(' ').toLowerCase();
      let score = item.score || 0;
      if (hay === needle || item.title.toLowerCase() === needle) {
        score = 1;
      } else if (hay.includes(needle) || needle.includes(item.title.toLowerCase())) {
        score = Math.max(score, 0.8);
      }
      if (!best || score > best.score) {
        best = { item, score };
      }
    });
    return best;
  }

  onQueryChange(value: string) {
    this.query = value;
    this.query$.next(value || '');
  }

  clearQuery() {
    this.query = '';
    this.query$.next('');
    if (this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
  }

  selectRecent(term: string) {
    this.query = term;
    this.query$.next(term);
  }

  clearRecent() {
    this.searchService.clearRecent();
    this.recent = [];
  }

  cleanText(value: string | null | undefined): string {
    return cleanGlobalSearchText(value);
  }

  iconFor(type: string): string {
    switch (type) {
      case 'application':
      case 'applicant':
        return 'fa-user';
      case 'report':
        return 'fa-file-alt';
      case 'subscriber':
        return 'fa-building';
      case 'user':
        return 'fa-user-cog';
      case 'property':
        return 'fa-home';
      case 'screening':
        return 'fa-balance-scale';
      case 'invoice':
        return 'fa-file-invoice-dollar';
      case 'queue':
        return 'fa-stream';
      case 'page':
        return 'fa-compass';
      default:
        return 'fa-search';
    }
  }

  onRowClick(row: GlobalSearchFlatRow) {
    this.activeIndex = row.flatIndex;
    this.activateCurrent();
  }

  onActionClick(action: GlobalSearchAction, item: GlobalSearchItem, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.searchService.rememberRecent(this.query || item.title);
    this.actions.run(action, item);
    this.close();
  }

  toggleActionMenu(index: number, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.actionMenuIndex = this.actionMenuIndex === index ? null : index;
    this.activeIndex = index;
  }

  private rebuildFlat() {
    const rows: GlobalSearchFlatRow[] = [];
    (this.groups || []).forEach((group) => {
      (group.items || []).forEach((item) => {
        rows.push({ groupLabel: group.label, item, flatIndex: rows.length });
      });
    });
    this.flatRows = rows;
    this.activeIndex = 0;
  }

  private moveActive(delta: number) {
    if (!this.flatRows.length) {
      return;
    }
    const next = this.activeIndex + delta;
    this.activeIndex = Math.max(0, Math.min(this.flatRows.length - 1, next));
    this.actionMenuIndex = null;
  }

  private activateCurrent() {
    if (!this.query.trim() && !this.flatRows.length) {
      return;
    }
    const row = this.flatRows[this.activeIndex];
    if (!row) {
      if (this.query && this.query.trim()) {
        const q = this.query.trim();
        this.searchService.rememberRecent(q);
        this.router.navigate(['/global-search'], { queryParams: { q } });
        this.close();
      }
      return;
    }
    this.searchService.rememberRecent(this.query || row.item.title);
    this.actions.runPrimary(row.item);
    this.close();
  }

  trackGroup(_i: number, g: GlobalSearchGroup) {
    return g.type + g.label;
  }

  trackItem(_i: number, item: GlobalSearchItem) {
    return item.type + ':' + item.id;
  }

  getFlatIndex(item: GlobalSearchItem): number {
    return this.flatRows.findIndex((row) => row.item === item || (row.item.type === item.type && row.item.id === item.id));
  }
}
